import type { CustomerSession } from "@shared-core/data/customer-api-types";
import { cn } from "../../app/components/primitives/utils";
import { PortalScopeNote } from "./PortalScopeNote";
import { POLLARDS_PHONE } from "./portalContact";

// What the Orders page is showing, said plainly, and how to change it.
//
// Two kinds of person (docs/features/customer-storefront/
// 12_decision_access_for_every_customer.md):
//  - Everyone has own work: orders they placed, quotes and proofs sent to
//    them. A short list must read as a boundary, not a fault, so the note says
//    what is and is not included, and how to get more.
//  - Someone staff have given "See all of the customer's work" sees the lot,
//    and can narrow the list to what they placed themselves. That is a view
//    over their own access, never a wider one.

export type OrdersView = "all" | "mine";

export function PortalOrdersScope({
  session,
  view,
  onViewChange,
}: {
  session: CustomerSession;
  view: OrdersView;
  onViewChange: (view: OrdersView) => void;
}) {
  // The session names the customer the person is working as.
  const customerName = session.customer_name;

  if (session.visibility === "customer_wide") {
    // "Other accounts" only when the customer has more than one: a CIS-only customer has none, and one with a
    // single Tharstern account has no others to include.
    const hasOtherAccounts = session.tharstern_account_codes.length > 1;
    const note =
      view === "all"
        ? hasOtherAccounts
          ? `Includes ${customerName}'s other accounts.`
          : null
        : "Orders placed by you, and quotes and proofs sent to you. Older orders our team took for you by phone or email may not show.";
    return (
      <div className="flex flex-col items-end gap-2">
        <div
          role="group"
          aria-label="Which orders to show"
          className="flex rounded-md border p-0.5"
        >
          {(
            [
              { id: "all", label: `All of ${customerName}'s orders` },
              { id: "mine", label: "Placed by you" },
            ] as const
          ).map((option) => (
            <button
              key={option.id}
              type="button"
              aria-pressed={view === option.id}
              onClick={() => onViewChange(option.id)}
              className={cn(
                "rounded px-3 py-1.5 text-xs font-medium transition-colors",
                view === option.id
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
        {note && (
          <p className="max-w-xs text-right text-xs text-muted-foreground">
            {note}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="flex max-w-sm flex-col items-end gap-2">
      <PortalScopeNote
        visibility={session.visibility}
        kind="orders"
        customerName={customerName}
      />
      <details className="w-full rounded-md border bg-card px-3 py-2 text-xs text-muted-foreground">
        <summary className="cursor-pointer font-medium text-foreground">
          Why am I not seeing everything?
        </summary>
        <div className="mt-2 space-y-1.5">
          <p>
            Showing orders placed by you, and quotes and proofs sent to you.
            Orders placed by colleagues aren&apos;t included. Older orders our
            team took for you by phone or email may not show either: ask your
            account manager if you need to see them.
          </p>
          <p>
            To see all of {customerName}&apos;s work, ask your Pollards contact
            to switch it on for you, or call {POLLARDS_PHONE}.
          </p>
        </div>
      </details>
    </div>
  );
}
